import * as React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';

import { SidebarMenuContext } from '~/components/Sidebar/SidebarMenu/context';
import {
  NavigationLevelPathConfig,
  SidebarMenuLevel,
} from '~/components/Sidebar/SidebarMenu/SidebarMenuLevel';
import { useHasChanged } from '~/hooks/useHasChanged';

type PropsType = {
  navigationRootPath: string;
  levelPaths: NavigationLevelPathConfig[];
};

export const SidebarMenu = (props: PropsType) => {
  const { navigationRootPath, levelPaths } = props;

  const { pathname } = useLocation();

  const { activeLevels, activeMenuPaths } = useMemo(() => {
    const activeLevels = pathname
      .replace(`${navigationRootPath}/`, '')
      .split('/');

    const activeMenuPaths = activeLevels
      .reduce((paths, level, index, activeLevels) => {
        const path = activeLevels.slice(0, index + 1).join('/');

        paths.push(path);

        return paths;
      }, [] as string[])
      .map((path) => `${navigationRootPath}/${path}`);

    return {
      activeMenuPaths,
      activeLevels,
    };
  }, [navigationRootPath, pathname]);

  const [openMenuItems, setOpenMenuItems] = useState<string[]>(activeMenuPaths);

  const hasActiveMenuPathsChanged = useHasChanged(
    JSON.stringify(activeMenuPaths),
  );
  useEffect(() => {
    if (hasActiveMenuPathsChanged) {
      setOpenMenuItems((current) => current.concat(activeMenuPaths));
    }
  }, [hasActiveMenuPathsChanged, activeMenuPaths]);

  const toggleOpenMenuItems = useCallback(
    (path: string) => {
      if (openMenuItems.includes(path)) {
        setOpenMenuItems(openMenuItems.filter((p) => p !== path));
      } else {
        setOpenMenuItems(openMenuItems.concat(path));
      }
    },
    [openMenuItems, setOpenMenuItems],
  );

  return (
    <SidebarMenuContext.Provider
      value={{
        pathname,
        navigationRootPath,
        openMenuItems,
        toggleOpenMenuItems,
      }}
    >
      <SidebarMenuLevel
        levelPaths={levelPaths}
        parentLevels={[]}
        activeLevels={activeLevels}
      />
    </SidebarMenuContext.Provider>
  );
};
